import Slider from "./Slider";
import dayjs from 'dayjs';
import StringHelper from "../helpers/StringHelper";


type State = {
    from: Date | null;
    to: Date | null;
}

/**
 * Класс по работе с кнопкой переключателем
 */
export default class Calendar {
    private $holder: JQuery;
    private $rowCalendarContainer: JQuery
    private currentDateIndex: number = 0;

    private ACTIVE_CLASS = 'active';
    private ACTIVE_FROM_CLASS = 'active-from';
    private ACTIVE_TO_CLASS = 'active-to';
    private BETWEEN_CLASS = 'between';
    private CURRENT_CLASS = 'current-day';
    private DISABLED_CLASS = 'disabled';
    private HOLIDAY_CLASS = 'holiday';

    private RIGHT_ACTIVE_CLASS = 'right-active-class'
    private LEFT_ACTIVE_CLASS = 'left-active-class'

    private selectors = {
        rowCalendar: '.js-row-calendar',
        rowDay: '.js-calendar-day',
        calendarSlider: '.js-slider-calendar',
    }

    private state: State = {
        from: null,
        to: null,
    }

    constructor(
        $holder: JQuery,
        private handleChangeDate: (
            state: State | null,
            ) => void,
        private dates: string[]
    ) {
        this.$holder = $holder;
        this.$rowCalendarContainer = this.$holder.find(this.selectors.rowCalendar);

        this.buildRowCalendar = this.buildRowCalendar.bind(this);
        this.showDayRowCalendar = this.showDayRowCalendar.bind(this);
        this.changeActiveDay = this.changeActiveDay.bind(this);
        this.setBetweenDay = this.setBetweenDay.bind(this);

        this.init();
    }

    public setState(newState: State): void {
        this.state = newState;
        this.setActiveElementsByState();
    }

    private findElementByDate(date: Date): JQuery | null {
        const $allDays = this.$holder.find(this.selectors.rowDay);

        for (let i = 0; i<= $allDays.length; i++) {
            const $dayElement = $($allDays[i]);
            const dayDate = $dayElement.data('day') as string;


            if (!dayDate) {
                continue;
            }

            if (date.getTime() !== new Date(dayDate).getTime()) {
                continue;
            }

            return $dayElement;
        }

        return null;
    }

    private setActiveElementsByState(): void {
        this.resetAllClasses();
        const $fromElement = this.state.from ? this.findElementByDate(this.state.from) : null;
        const $toElement = this.state.to ? this.findElementByDate(this.state.to) : null;

        if (!$fromElement && !$toElement) {
            return;
        }

        if ($fromElement) {
            $fromElement.addClass(this.ACTIVE_CLASS);
            $fromElement.addClass(this.ACTIVE_FROM_CLASS);
        }

        if ($toElement) {
            $toElement.addClass(this.ACTIVE_CLASS);
            $toElement.addClass(this.ACTIVE_TO_CLASS);
        }

        if ($toElement && $fromElement) {
            this.setBetweenDay();
        }
    }

    private resetAllClasses(): void {
        const $allDays = this.$holder.find(this.selectors.rowDay);
        $allDays.removeClass(this.RIGHT_ACTIVE_CLASS);
        $allDays.removeClass(this.LEFT_ACTIVE_CLASS);
        $allDays.removeClass(this.ACTIVE_CLASS);
        $allDays.removeClass(this.ACTIVE_FROM_CLASS);
        $allDays.removeClass(this.ACTIVE_TO_CLASS);
        $allDays.removeClass(this.BETWEEN_CLASS);
    }


    /**
     * Инициализация
     * @private
     */
    private init(): void
    {
        this.buildRowCalendar();

        const $sliderContainer = this.$holder.find(this.selectors.calendarSlider);
        const config = {
            initialSlide: this.currentDateIndex
        }

        $sliderContainer.data('config', config);
        new Slider($sliderContainer);

        this.$holder.on('click', this.selectors.rowDay, this.changeActiveDay);
    }

    private buildRowCalendar(): void {
        const currentDate = new Date();

        const nextMonthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);

        // Определяем количество дней в текущем месяце
        const daysInCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();

        // Определяем количество дней в следующем месяце
        const daysInNextMonth = new Date(nextMonthDate.getFullYear(), nextMonthDate.getMonth() + 1, 0).getDate();

        // Создаем массив дней текущего месяца
        const daysArray = Array.from({ length: daysInCurrentMonth }, (_, element) => element + 1);

        // Создаем массив дней следующего месяца
        const nextMonthDaysArray = Array.from({ length: daysInNextMonth }, (_, element) => element + 1);

        //добавляем дни текущего месяца
        this.showDayRowCalendar(daysArray, currentDate, true);

        //данные по следующему месяцу
        const nextMonthData = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
        //данные по 2 месяцу от текущего
        const moreNexMonthData = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 1);

        //название месяцев на русском
        const nextMonthName = StringHelper.capitalize(nextMonthData.toLocaleString('ru-RU', { month: 'long' })).slice(0, 3);
        const moreNextMonthName = StringHelper.capitalize(moreNexMonthData.toLocaleString('ru-RU', { month: 'long' })).slice(0, 3);

        //добавляем следующий месяц
        const nextMonth = document.createElement('div');
        nextMonth.className = 'calendar-row__month text-caption text-green-dark swiper-slide js-slider-item';
        nextMonth.textContent = nextMonthName;
        this.$rowCalendarContainer.get(0).appendChild(nextMonth);

        this.showDayRowCalendar(nextMonthDaysArray, nextMonthDate);

        //добавляем 2 месяц после текущего
        const moreNextMonth = document.createElement('div');
        moreNextMonth.className = 'calendar-row__month text-caption text-green-dark swiper-slide js-slider-item';
        moreNextMonth.textContent = moreNextMonthName;
        this.$rowCalendarContainer.get(0).appendChild(moreNextMonth);

        //находим текущее число
        this.addClassByCurrentDay(currentDate);
    }

    /**
     * Добавляем класс текущему дню
     * @param currentDate
     * @private
     */
    private addClassByCurrentDay(currentDate: Date): void {
        // Форматирование даты в нужный формат для атрибута data-day
        const currentDateTest = dayjs().format('DD.MM.YYYY');

        const $allDays = this.$holder.find(this.selectors.rowDay);

        $allDays.each((_, element) => {
            const elementDate = $(element).data('day') as string;

            if (!elementDate) {
                return;
            }

            const elementDateFormat = dayjs(new Date(elementDate)).format('DD.MM.YYYY');

            if (elementDateFormat === currentDateTest) {
                $(element).addClass(this.CURRENT_CLASS);
            }

        });
    }

    /**
     * Выводим дни
     * @param days
     * @param monthDate
     * @param isCurrent
     * @private
     */
    private showDayRowCalendar(days: number[], monthDate: Date, isCurrent = false): void {
        const currentDay = new Date().getDate();

        const daysOfWeek = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

        days.forEach(day => {
            const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
            const dayOfWeek = date.getDay();
            const dayElement = document.createElement('div');
            dayElement.className = 'calendar-row__day js-calendar-day swiper-slide js-slider-item';

            dayElement.setAttribute(
                'data-day',
                date.toString(),
            );

            const dayNumberSpan = document.createElement('span');
            dayNumberSpan.className = 'calendar-row__day-number h4';
            dayNumberSpan.textContent = day.toString();

            const dayOfWeekSpan = document.createElement('span');
            dayOfWeekSpan.className = 'calendar-row__day-of-week text-caption text-gray';
            dayOfWeekSpan.textContent = daysOfWeek[dayOfWeek];

            const SUNDAY_INDEX = 0;
            const SATURDAY_INDEX = 6;

            if (dayOfWeek === SUNDAY_INDEX || dayOfWeek === SATURDAY_INDEX) {
                dayOfWeekSpan.className = dayOfWeekSpan.className + ' ' + this.HOLIDAY_CLASS;
            }
            const formatDate = dayjs(date).format('DD.MM.YYYY');
            const hasEvents = this.dates.includes(formatDate);

            // Добавляем класс 'disabled' ко всем элементам до текущего числа
            if ((monthDate.getMonth() === monthDate.getMonth() && day < currentDay && isCurrent) || !hasEvents) {
                dayElement.classList.add(this.DISABLED_CLASS);
            }

            if (hasEvents) {
                dayElement.classList.add('has-event');
            }

            dayElement.appendChild(dayNumberSpan);
            dayElement.appendChild(dayOfWeekSpan);

            this.$rowCalendarContainer.get(0).appendChild(dayElement);
        });
    }

    private callHandlerChangeDate(): void {
        const $activeDays = this.$holder.find(`${this.selectors.rowDay}.${this.ACTIVE_CLASS}`)

        if (!$activeDays.length) {
            this.state = {
                from: null,
                to: null,
            };

            this.handleChangeDate(null);
            return;
        }

        const $fromElement = $activeDays.eq(0);
        const $toElement = $activeDays.eq(1);

        const fromDate = $fromElement.data('day') as string ?? null;
        const toDate = $toElement.data('day') as string ?? null;
        const stateForEmit = {
            from: null,
            to: null,
        };

        if (fromDate) {
            stateForEmit.from = new Date(fromDate);
        }

        if (toDate) {
            stateForEmit.to = new Date(toDate);
        }


        this.handleChangeDate(stateForEmit);
    }

    /**
     * Меняем активность у дней
     * @param event
     * @private
     */
    //TODO Нужно все состояния вынести из классов и держать в классе
    private changeActiveDay(event: Event): void {
        const $target = $(event.currentTarget);

        if($target.hasClass(this.DISABLED_CLASS)) {
            return;
        }

        const $allDays = this.$holder.find(this.selectors.rowDay);

        if (
            $allDays
            .filter((_, item) => $(item)
                .hasClass(this.ACTIVE_CLASS)).length >= 2
        ) {
            this.resetAllClasses();
        }

        $target.toggleClass(this.ACTIVE_CLASS);

        if (
            $allDays
                .filter((_, item) => $(item)
                .hasClass(this.ACTIVE_CLASS)).length >= 2
        ) {
            this.setBetweenDay();
        }

        this.callHandlerChangeDate();
    }

    /**
     * Устанавливаем дни между активными
     * @private
     */
    private setBetweenDay(): void {
        const beforeActiveDays = this.$holder.get(0).querySelectorAll('.active ~ div');

        let foundActive = false;

        const $activeElements = this.$holder.get(0).querySelectorAll('.js-calendar-day.active');

        $($activeElements).eq(0).addClass(this.RIGHT_ACTIVE_CLASS);
        $($activeElements).eq(1).addClass(this.LEFT_ACTIVE_CLASS);

        beforeActiveDays.forEach((element) => {
            if (foundActive) {
                return;
            }

            if ($(element).hasClass(this.ACTIVE_CLASS)) {
                foundActive = true;
                return;
            }
            $(element).addClass(this.BETWEEN_CLASS);
        });
    }
}
